import { formatSnakeCaseToPascalCase } from "../../common/utils/formatter";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import { getProjectSubRouteTemplate, MIGRATE_OPS_DOCS_SUBROUTE, MIGRATE_OPS_SUBROUTE, PROJECT_SUBROUTE } from "../app/AppRoutes";
import Azure from "../../assets/integration_logos/azure.png";
import AWS from "../../assets/integration_logos/aws.png";
import { ImTree } from "react-icons/im";
import { OperationRecipeID } from "gc-web-proto/galaxycompletepb/operationpb/operation_pb";
import ProtoDoc from "../../assets/migrateOps/migrateops-proto.json";
import { IntegrationModule } from "gc-web-proto/galaxycompletepb/apipb/domainpb/integration_pb";
import { ColumnSortFilterConfig, ListFilterableField } from "../core/data/ListData";
import { ListProjectOperations } from "gc-web-proto/galaxycompletepb/apipb/operation_api_pb";
import { FilterParams } from "gc-web-proto/galaxycompletepb/commonpb/datafilter_pb";
import { SortState } from "../../common/table/DataTable";
import { OperationStatus, OperationStatusFilter } from "gc-web-proto/galaxycompletepb/apipb/domainpb/enumpb/operation_status_pb";
import { appI18n } from "../i18n/i18n";

export const generateOpDetailsPath = (opId: number | string, projectId: string) => {
    return generatePath(getProjectSubRouteTemplate(PROJECT_SUBROUTE.MIGRATE_OPS) + `/${opId}`, { projectId });
};

export const useNavigateToOpDetails = () => {
    const navigate = useNavigate();
    const { projectId } = useParams();
    return (opId: number) => {
        navigate(generateOpDetailsPath(opId, projectId));
    };
};

export const useNavigateToOpDocs = () => {
    const navigate = useNavigate();
    const { projectId } = useParams();
    const p = generatePath(getProjectSubRouteTemplate(PROJECT_SUBROUTE.MIGRATE_OPS_DOCS), { projectId });
    return () => navigate(p);
};

export const useNavigatetoRecipeReference = () => {
    const navigate = useNavigate();
    const { projectId } = useParams();
    return (recipeId: OperationRecipeID, view?: "reference" | "editor") => {
        const p = generatePath(getProjectSubRouteTemplate(PROJECT_SUBROUTE.MIGRATE_OPS_DOCS) + `/${recipeId}` + (view ? `?view=${view}` : ""), {
            projectId,
        });
        navigate(p);
    };
};

export const openRecipeReferenceInNewWindow = (recipeId: OperationRecipeID, projectId: string) => {
    const p = generatePath(getProjectSubRouteTemplate(PROJECT_SUBROUTE.MIGRATE_OPS_DOCS) + `/${recipeId}`, { projectId });
    window.open(p, "_blank");
};

export const useNavgiateToRecipeBuilder = () => {
    const navigate = useNavigate();
    const { projectId } = useParams();
    return (recipeId: OperationRecipeID) => {
        const p = generatePath(getProjectSubRouteTemplate(PROJECT_SUBROUTE.MIGRATE_OPS) + `/${MIGRATE_OPS_SUBROUTE.WIZARD}` + `/${recipeId}`, { projectId });
        navigate(p);
    };
};

export const useNavigateToOpsList = () => {
    const navigate = useNavigate();
    const { projectId } = useParams();
    const p = generatePath(getProjectSubRouteTemplate(PROJECT_SUBROUTE.MIGRATE_OPS), { projectId });
    return () => navigate(p);
};

export const useNavigateToNewOpScreen = () => {
    const navigate = useNavigate();
    const { projectId } = useParams();
    const p = generatePath(getProjectSubRouteTemplate(PROJECT_SUBROUTE.MIGRATE_OPS) + `/${MIGRATE_OPS_SUBROUTE.CREATE}`, {
        projectId,
    });
    return () => navigate(p);
};

export const useNavigateToNewOpBuilder = () => {
    const navigate = useNavigate();
    const { projectId } = useParams();
    const p = generatePath(getProjectSubRouteTemplate(PROJECT_SUBROUTE.MIGRATE_OPS) + `/${MIGRATE_OPS_SUBROUTE.BUILDER}`, {
        projectId,
    });
    return () => navigate(p);
};

export const useNavigateToOpInstructions = () => {
    const navigate = useNavigate();
    const { projectId } = useParams();

    return (recipeId: OperationRecipeID) => {
        const p = generatePath(
            getProjectSubRouteTemplate(PROJECT_SUBROUTE.MIGRATE_OPS_DOCS) + `/${MIGRATE_OPS_DOCS_SUBROUTE.GETTING_STARTED}` + `/${recipeId}`,
            {
                projectId,
            }
        );
        navigate(p);
    };
};
export const useNavigateToReference = () => {
    const navigate = useNavigate();
    const { projectId } = useParams();
    const p = generatePath(getProjectSubRouteTemplate(PROJECT_SUBROUTE.MIGRATE_OPS_DOCS), {
        projectId,
    });
    return () => navigate(p);
};

export const useNavigateToHostActionsList = () => {
    const navigate = useNavigate();
    const { projectId } = useParams();
    const p = generatePath(getProjectSubRouteTemplate(PROJECT_SUBROUTE.MIGRATE_OPS_HOST_ACTIONS), { projectId });
    return () => navigate(p);
};

export const useNavigateToNewHostAction = () => {
    const navigate = useNavigate();
    const { projectId } = useParams();
    const p = generatePath(getProjectSubRouteTemplate(PROJECT_SUBROUTE.MIGRATE_OPS_HOST_ACTIONS) + `/new`, { projectId });
    return () => navigate(p);
};

export const useNavigateToHostActionDetails = () => {
    const navigate = useNavigate();
    const { projectId } = useParams();
    return (hostActionReference: string) => {
        const p = generatePath(getProjectSubRouteTemplate(PROJECT_SUBROUTE.MIGRATE_OPS_HOST_ACTIONS) + `/${hostActionReference}`, { projectId });
        navigate(p);
    };
};

export const useNavigateToProjectHostActionTestExecutionDetails = () => {
    const navigate = useNavigate();
    const { projectId } = useParams();
    return (hostActionReference: string, executionId: number) => {
        const p = generatePath(getProjectSubRouteTemplate(PROJECT_SUBROUTE.MIGRATE_OPS_HOST_ACTIONS) + `/${hostActionReference}` + `/${executionId}`, {
            projectId,
        });
        navigate(p);
    };
};

export const useNavigateToMigrateOpsXrays = () => {
    const navigate = useNavigate();
    const { projectId } = useParams();
    const p = generatePath(getProjectSubRouteTemplate(PROJECT_SUBROUTE.MIGRATE_OPS_XRAYS), { projectId });
    return () => navigate(p);
};
export const getOperationRecipeLabel = (opRecipe?: OperationRecipeID) => {
    if (opRecipe === undefined || opRecipe === null) {
        return appI18n.t("migrateops:recipes.other");
    }
    const operationProto = ProtoDoc.files.find((file) => file.name === "galaxycompletepb/operationpb/operation.proto");
    const recipeEnumValues = operationProto.enums.find((e) => e.name === "OperationRecipeID").values;
    const recipe = recipeEnumValues.find((v: any) => v.number === opRecipe.toString());

    if (!recipe?.description) {
        return appI18n.t("migrateops:recipes.other");
    }
    return appI18n.t(`migrateops:recipes.byId.${recipe.name.toLowerCase()}.label`, {
        defaultValue: recipe.description,
    });
};

export const getOperationRecipeDescription = (opRecipe?: OperationRecipeID) => {
    if (opRecipe === undefined || opRecipe === null) {
        return "";
    }
    const operationProto = ProtoDoc.files.find((file) => file.name === "galaxycompletepb/operationpb/operation.proto");
    const recipeEnumValues = operationProto.enums.find((e) => e.name === "OperationRecipeID").values;
    const recipeKey = recipeEnumValues.find((v: any) => v.number === opRecipe.toString())?.name.toLowerCase();
    const description = operationProto.messages.find((m) => m.name === "OperationConfig")?.fields.find((f) => f.name === recipeKey)?.description;
    return description
        ? appI18n.t(`migrateops:recipes.byId.${recipeKey}.description`, {
              defaultValue: description,
          })
        : "";
};

export const getOperationRecipeIdString = (opRecipe?: OperationRecipeID) => {
    if (opRecipe === undefined || opRecipe === null) {
        return "";
    }
    const operationProto = ProtoDoc.files.find((file) => file.name === "galaxycompletepb/operationpb/operation.proto");
    const recipeEnumValues = operationProto.enums.find((e) => e.name === "OperationRecipeID").values;
    return recipeEnumValues.find((v: any) => v.number === opRecipe.toString())?.name;
};

export const getOperationRecipeFromIntegrationModule = (module: IntegrationModule) => {
    if (module === IntegrationModule.NUTANIX) {
        return OperationRecipeID.MIGRATEOPS_NUTANIX_COMPUTE;
    }
    if (module === IntegrationModule.AWS) {
        return OperationRecipeID.MIGRATEOPS_AWS_COMPUTE;
    }
    if (module === IntegrationModule.AZURE) {
        return OperationRecipeID.MIGRATEOPS_AZURE_COMPUTE;
    }
    if (module === IntegrationModule.NATIVEEDGE) {
        return OperationRecipeID.MIGRATEOPS_NATIVEEDGE_COMPUTE;
    }
    // if (module === IntegrationModule.HYPERV) {
    //     return OperationRecipeID.MIGRATEOPS_HYPERV_COMPUTE;
    // }
};

export const getOperationRecipeIcon = (opRecipe: OperationRecipeID) => {
    if (opRecipe === OperationRecipeID.MIGRATEOPS_AWS_COMPUTE) {
        return <img alt={"AWS"} src={AWS} style={{ width: 60 }} />;
    }
    if (opRecipe === OperationRecipeID.MIGRATEOPS_AZURE_COMPUTE) {
        return <img alt={"Azure"} src={Azure} style={{ width: 60 }} />;
    }
    if (opRecipe === OperationRecipeID.MIGRATEOPS_LOCAL_DATA_MIGRATION) {
        return <ImTree size={60} />;
    }
};

interface OperationRecipeConfig {
    label: string;
    description: string;
    icon: React.ReactNode;
}

export const getOperationRecipeConfig = (opRecipe: OperationRecipeID): OperationRecipeConfig => {
    if (opRecipe === OperationRecipeID.MIGRATEOPS_AZURE_COMPUTE) {
        return {
            label: getOperationRecipeLabel(opRecipe),
            description: appI18n.t("migrateops:recipes.placeholderDescriptions.azure"),
            icon: getOperationRecipeIcon(opRecipe),
        };
    }
    if (opRecipe === OperationRecipeID.MIGRATEOPS_AWS_COMPUTE) {
        return {
            label: getOperationRecipeLabel(opRecipe),
            description: appI18n.t("migrateops:recipes.placeholderDescriptions.aws"),
            icon: getOperationRecipeIcon(opRecipe),
        };
    }
    if (opRecipe === OperationRecipeID.MIGRATEOPS_LOCAL_DATA_MIGRATION) {
        return {
            label: getOperationRecipeLabel(opRecipe),
            description: appI18n.t("migrateops:recipes.placeholderDescriptions.local"),
            icon: getOperationRecipeIcon(opRecipe),
        };
    }
};

export const matchOperationRecipeIdKeyToConfigField = (recipeIdKey: string) => {
    const formattedId = formatSnakeCaseToPascalCase(recipeIdKey.replace("MIGRATEOPS_", ""));
    return `${formattedId}.Config`;
};

const getListSortFieldLabel = (field: ListProjectOperations.Request.Sort.Field) => {
    if (field === ListProjectOperations.Request.Sort.Field.CREATED_AT) {
        return appI18n.t("migrateops:list.sort.createdAt");
    } else if (field === ListProjectOperations.Request.Sort.Field.OPERATION_NAME) {
        return appI18n.t("migrateops:list.fields.operationName");
    } else if (field === ListProjectOperations.Request.Sort.Field.RECIPE_NAME) {
        return appI18n.t("migrateops:list.fields.recipeName");
    }
};

const getListFilterFieldLabel = (field: ListProjectOperations.Request.Filter.FieldCase) => {
    if (field === ListProjectOperations.Request.Filter.FieldCase.OPERATION_NAME) {
        return appI18n.t("migrateops:list.fields.operationName");
    } else if (field === ListProjectOperations.Request.Filter.FieldCase.RECIPE_NAME) {
        return appI18n.t("migrateops:list.fields.recipeName");
    } else if (field === ListProjectOperations.Request.Filter.FieldCase.OPERATION_LABEL) {
        return appI18n.t("migrateops:list.fields.operationLabel");
    } else if (field === ListProjectOperations.Request.Filter.FieldCase.NOTES) {
        return appI18n.t("migrateops:list.fields.notes");
    } else if (field === ListProjectOperations.Request.Filter.FieldCase.STATUS) {
        return appI18n.t("migrateops:list.fields.status");
    }
};

export const getOperationListFilterConfig: () => ColumnSortFilterConfig<ListProjectOperations.Request, ListProjectOperations.Request.Sort, any, any> = () => {
    return {
        sort: {
            defaultSort: {
                fieldId: ListProjectOperations.Request.Sort.Field.CREATED_AT,
                label: getListSortFieldLabel(ListProjectOperations.Request.Sort.Field.CREATED_AT),
            },
            defaultSortDesc: true,
            sortFields: [
                {
                    fieldId: ListProjectOperations.Request.Sort.Field.CREATED_AT,
                    label: getListSortFieldLabel(ListProjectOperations.Request.Sort.Field.CREATED_AT),
                },
                {
                    fieldId: ListProjectOperations.Request.Sort.Field.OPERATION_NAME,
                    label: getListSortFieldLabel(ListProjectOperations.Request.Sort.Field.OPERATION_NAME),
                },
                {
                    fieldId: ListProjectOperations.Request.Sort.Field.RECIPE_NAME,
                    label: getListSortFieldLabel(ListProjectOperations.Request.Sort.Field.RECIPE_NAME),
                },
            ],
            getSortParam: (s: SortState) => new ListProjectOperations.Request.Sort().setField(s.field.fieldId).setDesc(s.descending.value),
        },
        filter: [
            {
                fieldId: ListProjectOperations.Request.Filter.FieldCase.OPERATION_NAME,
                label: getListFilterFieldLabel(ListProjectOperations.Request.Filter.FieldCase.OPERATION_NAME),
                filterType: "string",
                addFilterToRequest: (r: ListProjectOperations.Request, f: FilterParams.StringFilter) => {
                    r.addFilterParams(new ListProjectOperations.Request.Filter().setOperationName(f));
                    console.log("filter", r.getFilterParamsList());
                },
            },
            {
                fieldId: ListProjectOperations.Request.Filter.FieldCase.RECIPE_NAME,
                label: getListFilterFieldLabel(ListProjectOperations.Request.Filter.FieldCase.RECIPE_NAME),
                filterType: "string",
                addFilterToRequest: (r: ListProjectOperations.Request, f: FilterParams.StringFilter) => {
                    r.addFilterParams(new ListProjectOperations.Request.Filter().setRecipeName(f));
                },
            },
            {
                fieldId: ListProjectOperations.Request.Filter.FieldCase.OPERATION_LABEL,
                label: getListFilterFieldLabel(ListProjectOperations.Request.Filter.FieldCase.OPERATION_LABEL),
                filterType: "simpleString",
                addFilterToRequest: (r: ListProjectOperations.Request, f: FilterParams.SimpleStringFilter) => {
                    r.addFilterParams(new ListProjectOperations.Request.Filter().setOperationLabel(f));
                },
            },
            {
                fieldId: ListProjectOperations.Request.Filter.FieldCase.NOTES,
                label: getListFilterFieldLabel(ListProjectOperations.Request.Filter.FieldCase.NOTES),
                filterType: "string",
                addFilterToRequest: (r: ListProjectOperations.Request, f: FilterParams.StringFilter) => {
                    r.addFilterParams(new ListProjectOperations.Request.Filter().setNotes(f));
                },
            },
            {
                fieldId: ListProjectOperations.Request.Filter.FieldCase.STATUS,
                label: getListFilterFieldLabel(ListProjectOperations.Request.Filter.FieldCase.STATUS),
                filterType: "enum",
                enumLabel: appI18n.t("migrateops:list.fields.operationStatus"),
                enumValuesList: Object.values(OperationStatus.OperationStatus),
                addFilterToRequest: (r: ListProjectOperations.Request, f: OperationStatusFilter) => {
                    const operationStatus = f.getHas();
                    if (operationStatus) {
                        r.addFilterParams(new ListProjectOperations.Request.Filter().setStatus(operationStatus));
                    }
                },
                getFormValueDisplayLabel: (v: OperationStatus.OperationStatus) => getOperationStatusDisplayName(v),
                createParamFromFormValue: (v: OperationStatus.OperationStatus) => new OperationStatusFilter().setHas(new OperationStatus().setValue(v)),
            },
        ],
    };
};

export const getOperationStatusDisplayName = (e: OperationStatus.OperationStatus): string => {
    if (e === OperationStatus.OperationStatus.PENDING) {
        return appI18n.t("common:status.pending");
    } else if (e === OperationStatus.OperationStatus.QUEUED) {
        return appI18n.t("common:status.queued");
    } else if (e === OperationStatus.OperationStatus.RUNNING) {
        return appI18n.t("common:status.running");
    } else if (e === OperationStatus.OperationStatus.WAITING) {
        return appI18n.t("common:status.waiting");
    } else if (e === OperationStatus.OperationStatus.AWAITING_USER_INPUT) {
        return appI18n.t("migrateops:status.awaitingUserInput");
    } else if (e === OperationStatus.OperationStatus.PAUSED) {
        return appI18n.t("common:status.paused");
    } else if (e === OperationStatus.OperationStatus.SKIPPED) {
        return appI18n.t("common:status.skipped");
    } else if (e === OperationStatus.OperationStatus.COMPLETED) {
        return appI18n.t("common:status.completed");
    } else if (e === OperationStatus.OperationStatus.FAILED) {
        return appI18n.t("common:status.failed");
    } else if (e === OperationStatus.OperationStatus.CANCELLED) {
        return appI18n.t("common:status.cancelled");
    } else if (e === OperationStatus.OperationStatus.UNKNOWN) {
        return appI18n.t("common:status.unknown");
    } else {
        return null;
    }
};
